/// <amd-module name="Repos/Views/PushDetails/PushDetailsService" />
import { getChangesCollection } from "Repos/Changes/Util";
import { getGitNavigationService } from "Repos/ChangesCommon/ChangesNavigationService";
import { IChangeCollection } from "Repos/ChangesCommon/Contracts";
import { GitPush, GitUserDate, GitVersionDescriptor, VersionControlChangeType } from "Repos/Common/Contracts/Git";
import { EMPTY_OBJECT_ID } from "Repos/Common/Util/Ref";
import { commitToVersionDescriptor, getVersionString } from "Repos/Common/Util/Version";
import { getGitRepository } from "Repos/Common/VCViewModelDataProviderService";
import * as Resources from "Repos/Views/PushDetails/Resources";
import { IObservableValue, IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { getGitClient } from "./RestClient/Git";

const diffCommitsDataProviderId = "ms.vss-code-web.diff-commits-data-provider";
const commitDetailsDataProviderId = "ms.vss-code-web.commit-details-view-data-provider";

interface GitChangeItem {
    isFolder: boolean;
    isSymLink: boolean;
    path?: string;
    serverItem?: string;
}

interface GitCommitChange {
    changeType: VersionControlChangeType;
    commitId: string;
    item: GitChangeItem;
    sourceServerItem?: string;
}

interface IBranchCompareDiffData {
    allChangesIncluded: boolean;
    changeCounts: { [changeType: number]: number };
    changes: GitCommitChange[];
    commitId: { full: string; short: string };
}

interface GitCommitObject {
    full: string;
    short: string;
}

interface GitCommitDetails {
    author: GitUserDate;
    changes: GitCommitChange[];
    comment: string;
    committer: GitUserDate;
    commitId: GitCommitObject | string;
    parents: string[];
    push: GitPush;
}

interface IDiffParentDetails {
    diffParent: GitCommitDetails;
    parentId: string;
}

interface ICommitDataProviderData {
    allChangesIncluded: boolean;
    commitDetails: GitCommitDetails;
    diffParentDetails?: IDiffParentDetails;
}

export interface IPushDetailsService extends IVssService {
    getBaseVersion(): GitVersionDescriptor | undefined;
    getChanges(): IReadonlyObservableValue<IChangeCollection<GitVersionDescriptor> | undefined>;
    getErrorMessage(): IReadonlyObservableValue<string | undefined>;
    getPushDate(): Date;
    getPusher(): IdentityRef;
    getReady(): IObservableValue<boolean>;
    getRefName(): string;
    getSelectedTabId(): IReadonlyObservableValue<string>;
    getTargetVersion(): GitVersionDescriptor;
    setSelectedTabId(selectedTabId: string): void;
}

class GitPushDetailsService extends VssService implements IPushDetailsService {
    private baseVersion: GitVersionDescriptor | undefined;
    private targetVersion: GitVersionDescriptor;
    private refName: string;
    private pusher: IdentityRef;
    private pushDate: Date;
    private selectedTabId: ObservableValue<string>;
    private ready = new ObservableValue<boolean>(false);
    private errorMessage = new ObservableValue<string | undefined>(undefined);
    private changes = new ObservableValue<IChangeCollection<GitVersionDescriptor> | undefined>(undefined);
    private maxNumberOfChanges = 1000;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const pageStateHistoryService = pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        const queryParams = pageStateHistoryService.getProjectedState();

        this.selectedTabId = new ObservableValue(queryParams["_a"] || "summary");
        pageStateHistoryService.registerObservable(
            this.selectedTabId,
            "_a",
            HistoryBehavior.replace,
            value => (value === "summary" ? "" : value),
            fromUrl => fromUrl || "summary"
        );

        getGitNavigationService(pageContext).setChanges(this.changes);

        this.getPushDetails();
    }

    public getBaseVersion(): GitVersionDescriptor | undefined {
        return this.baseVersion;
    }

    public getChanges(): IReadonlyObservableValue<IChangeCollection<GitVersionDescriptor> | undefined> {
        return this.changes;
    }

    public getErrorMessage(): IReadonlyObservableValue<string | undefined> {
        return this.errorMessage;
    }

    public getPushDate(): Date {
        return this.pushDate;
    }

    public getPusher(): IdentityRef {
        return this.pusher;
    }

    public getReady(): IObservableValue<boolean> {
        return this.ready;
    }

    public getRefName(): string {
        return this.refName;
    }
    public getSelectedTabId(): IReadonlyObservableValue<string> {
        return this.selectedTabId;
    }

    public getTargetVersion(): GitVersionDescriptor {
        return this.targetVersion;
    }

    public setSelectedTabId(selectedTabId: string): void {
        this.selectedTabId.value = selectedTabId;
    }

    private async getPushDetails() {
        const pageStateHistoryService = this.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        const repository = getGitRepository(this.pageContext)!;

        try {
            const pushIdString = pageStateHistoryService.getProjectedState()["parameters"];
            const pushId = parseInt(pushIdString);

            if (isNaN(pushId)) {
                throw new Error(format(Resources.PushIdNaNError, pushIdString));
            }

            const push = await getGitClient(this.pageContext).getPush(repository.id, pushId, undefined, undefined, true);

            this.refName = push.refUpdates[0].name;
            const baseVersion = commitToVersionDescriptor(push.refUpdates[0].oldObjectId);
            this.baseVersion = baseVersion.version === EMPTY_OBJECT_ID ? undefined : baseVersion;
            this.targetVersion = commitToVersionDescriptor(push.refUpdates[push.refUpdates.length - 1].newObjectId);
            this.pusher = push.pushedBy;
            this.pushDate = push.date;

            this.fetchChanges(push);

            this.ready.value = true;
        } catch (ex) {
            this.errorMessage.value = ex.message;
        }
    }

    private async fetchChanges(pushData: GitPush) {
        const repository = getGitRepository(this.pageContext)!;

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        let changes: GitCommitChange[] | undefined;
        let allChangesIncluded: boolean = true;

        if (this.baseVersion) {
            const data = await contributionService.getDataAsync<IBranchCompareDiffData>(diffCommitsDataProviderId, {
                baseVersion: getVersionString(this.baseVersion),
                maxNumberOfChanges: this.maxNumberOfChanges,
                repositoryId: repository.id,
                skipCount: 0,
                targetVersion: getVersionString(this.targetVersion)
            });

            if (data) {
                changes = data.changes;
                allChangesIncluded = data.allChangesIncluded;
            }
        } else {
            const data = await contributionService.getDataAsync<ICommitDataProviderData>(commitDetailsDataProviderId, {
                commitVersion: pushData.refUpdates[pushData.refUpdates.length - 1].newObjectId,
                repoName: repository.name,
                projectName: repository.project.name,
                maxNumberOfChanges: this.maxNumberOfChanges,
                skipCount: 0
            });
            if (data) {
                changes = data.commitDetails.changes;
                allChangesIncluded = data.allChangesIncluded;
            }
        }

        this.changes.value = getChangesCollection(
            changes
                ? changes.map(c => {
                      return {
                          changeType: c.changeType,
                          commitId: c.commitId,
                          item: {
                              isFolder: c.item.isFolder,
                              isSymLink: c.item.isSymLink,
                              originalPath: c.item.serverItem,
                              path: c.item.path || c.item.serverItem!
                          }
                      };
                  })
                : [],
            repository.name,
            pushData.refUpdates[pushData.refUpdates.length - 1].newObjectId,
            this.targetVersion,
            commitToVersionDescriptor(pushData.refUpdates[0].oldObjectId),
            !allChangesIncluded
                ? () => {
                      this.maxNumberOfChanges = this.maxNumberOfChanges * 2;
                      this.fetchChanges(pushData);
                  }
                : undefined
        );
    }
}

const serviceName = "IPushDetailsService";
export function getPushDetailsService(context: IVssPageContext) {
    return context.getService<IPushDetailsService>(serviceName);
}

Services.add(serviceName, { serviceFactory: GitPushDetailsService });
